import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, LayoutPanelTop } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { ChatPageAccess, ChatPageAccessDto, PublicUrlDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { pushGtmEvent } from '@/Config/libs/analytics';
import type { Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  RadioGroupSegmented,
  RadioGroupSegmentedItem,
} from '@/Domain/components/Shared/RadioGroup/RadioGroup';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { WidgetScript } from '@/Domain/components/WidgetScript';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { ImplementationData } from '@/UI/Website/WidgetSettings.page';

/** "Open" chip rendered inline next to a section title; hidden on `sm+` unless
 *  `alwaysVisible` (Chat Page has no preview pane). The visible text says "Open"
 *  rather than the destination name so it doesn't collide with the section's
 *  own <Label> when test queries match by text — full destination name still
 *  rides along via `aria-label` for screen readers. */
const SectionOpenLink = ({
  link,
  label,
  testid,
  openLabel,
  alwaysVisible = false,
}: {
  link: string | undefined;
  label: string;
  testid: string;
  openLabel: string;
  alwaysVisible?: boolean;
}) => (
  <Button
    variant="ghost"
    size="xs"
    data-testid={testid}
    disabled={!link}
    onClick={() => link && window.open(window.location.origin + link)}
    aria-label={label}
    className={`shrink-0 gap-1.5 text-muted-foreground ${alwaysVisible ? '' : 'sm:hidden'}`}
  >
    {openLabel}
    <ExternalLink className="h-3.5 w-3.5" />
  </Button>
);

const WIDGET_SCRIPT_URL = 'https://bubble.quickchat.ai/chat.js';

function createWidgetSnippet(scenarioId: ImplementationData['scenario_id']): string {
  return `<script>
  (function(e,a,d,i,c,t=a.createElement(d)){e[c]=e[c]||function(){(e[c].q=e[c].q||[]).push(arguments)},t.src=i,t.async=1,a.body.insertAdjacentElement("beforeend",t)})(window,document,"script","${WIDGET_SCRIPT_URL}","_quickchat");
  _quickchat("init", "${scenarioId}");
</script>`;
}

function createEmbedSnippet(scenarioId: ImplementationData['scenario_id']): string {
  return `<div id="quickchat-embedded" style="height:600px;overflow:hidden;"></div>
<script>
  (function(e,a,d,i,c,t=a.createElement(d)){e[c]=e[c]||function(){(e[c].q=e[c].q||[]).push(arguments)},t.src=i,t.async=1,a.body.insertAdjacentElement("beforeend",t)})(window,document,"script","${WIDGET_SCRIPT_URL}","_quickchat");
  _quickchat("containerId", "quickchat-embedded");
  _quickchat("init", "${scenarioId}");
</script>`;
}

function createShopifyWidgetSnippet(scenarioId: ImplementationData['scenario_id']): string {
  return `<script>
  window.__quickchat_shopify_cart_token = {{ cart.token | json }};
  window.__quickchat_shopify_country = {{ localization.country.iso_code | json }};
  window.__quickchat_shopify_language = {{ localization.language.iso_code | json }};
</script>
<script>
  (function(e,a,d,i,c,t=a.createElement(d)){e[c]=e[c]||function(){(e[c].q=e[c].q||[]).push(arguments)},t.src=i,t.async=1,a.body.insertAdjacentElement("beforeend",t)})(window,document,"script","${WIDGET_SCRIPT_URL}","_quickchat");
  (function () {
    var params = {};
    var rendered = function (v) { return v && String(v).indexOf("{{") === -1; };
    if (rendered(window.__quickchat_shopify_cart_token)) params.shopify_cart_token = window.__quickchat_shopify_cart_token;
    if (rendered(window.__quickchat_shopify_country)) params.shopify_country = window.__quickchat_shopify_country;
    if (rendered(window.__quickchat_shopify_language)) params.shopify_language = window.__quickchat_shopify_language;
    if (Object.keys(params).length > 0) _quickchat("customParams", params);
  })();
  _quickchat("init", "${scenarioId}");
</script>`;
}

export const Install = () => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const { capture } = usePostHogAnalytics();

  const { data: implementation } = useQuery<ImplementationData>({ queryKey: ['implementation'] });
  const { data: integrations } = useQuery<Integrations>({ queryKey: ['integrations'] });

  const form = useForm<PublicUrlDto>({
    defaultValues: { public_url_active: implementation?.public_url_active ?? false },
  });

  const mutation = useMutation<PublicUrlDto, Error, PublicUrlDto>({
    mutationKey: [QueryKey.integration.publicUrl],
  });

  useAutoSubmit<PublicUrlDto>(form, (data) => mutation.mutate(data as PublicUrlDto), false);

  const { data: chatPageAccessData } = useQuery<ChatPageAccessDto>({
    queryKey: [QueryKey.chatPageAccess.get],
  });
  const chatPageAccessMutation = useMutation<ChatPageAccessDto, Error, ChatPageAccess>({
    mutationKey: [QueryKey.chatPageAccess.patch],
    onError: () => toast.error(t('website:chat_page.error')),
  });

  return (
    <div className="relative flex flex-col gap-8">
      {/* Section order: master Widget & Embed Access toggle on top (the
          "is everything turned on at all?" switch), then per-channel install
          blocks (Widget → Embed → Chat Page), then Chat Page Access at the
          bottom. On <sm each Widget/Embed section carries its own "Open in
          new tab" CTA because the right-side live preview pane isn't
          rendered on phones. */}
      <Item variant="muted" className="w-full" size="sm">
        <ItemContent>
          <ItemTitle className="line-clamp-1">{t('website:widget_embed_access.title')}</ItemTitle>
          <ItemDescription>{t('website:widget_embed_access.description')}</ItemDescription>
        </ItemContent>
        <ItemActions className="shrink-0">
          <Controller
            name="public_url_active"
            control={form.control}
            render={({ field }) => (
              <Switch id={field.name} checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </ItemActions>
      </Item>

      {implementation?.has_shopify_mcp_action && (
        <div className="relative space-y-1.5" data-testid="shopify-widget-snippet">
          <Label>{t('web_widget_shopify.label')}</Label>
          <p className="text-sm text-muted-foreground">{t('web_widget_shopify.description')}</p>
          <WidgetScript
            script={createShopifyWidgetSnippet(implementation.scenario_id)}
            onCopy={() => {
              capture(PostHogEventEnum.WidgetSnippetCopied, { snippet_type: 'widget_shopify' });
              pushGtmEvent(GtmEventEnum.WidgetSnippetCopied, {
                snippet_type: 'widget_shopify',
              });
            }}
          />
        </div>
      )}

      <div className="relative space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label>{t('web_widget.label')}</Label>
          <SectionOpenLink
            link={integrations?.links.widget}
            label={t('website:preview_controls.widget')}
            openLabel={t('website:preview_link.open')}
            testid="install-open-widget"
          />
        </div>
        <p className="text-sm text-muted-foreground">{t('web_widget.description')}</p>
        {implementation != null && (
          <WidgetScript
            script={createWidgetSnippet(implementation.scenario_id)}
            onCopy={() => {
              capture(PostHogEventEnum.WidgetSnippetCopied, { snippet_type: 'widget' });
              pushGtmEvent(GtmEventEnum.WidgetSnippetCopied, { snippet_type: 'widget' });
            }}
          />
        )}
      </div>

      <div className="relative space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label>{t('embed_integration.label')}</Label>
          <SectionOpenLink
            link={integrations?.links.embed}
            label={t('website:preview_controls.embed')}
            openLabel={t('website:preview_link.open')}
            testid="install-open-embed"
          />
        </div>
        <p className="text-sm text-muted-foreground">{t('embed_integration.description')}</p>
        {implementation != null && (
          <WidgetScript
            script={createEmbedSnippet(implementation.scenario_id)}
            onCopy={() => {
              capture(PostHogEventEnum.WidgetSnippetCopied, { snippet_type: 'embed' });
              pushGtmEvent(GtmEventEnum.WidgetSnippetCopied, { snippet_type: 'embed' });
            }}
          />
        )}
      </div>

      <div className="relative space-y-1.5" data-testid="chat-page-link-section">
        <Label>{t('website:chat_page_link.title')}</Label>
        <p className="text-sm text-muted-foreground">{t('website:chat_page_link.description')}</p>
        {/* Chat Page is publicly shareable and has no preview frame, so this
            full-width "Open" CTA is the section's primary action — shown at
            every viewport (the matching Widget/Embed actions are inline
            next-to-title because those sections have their own scripts and a
            desktop preview frame). */}
        <Button
          variant="secondary"
          size="md"
          data-testid="install-open-chat-page"
          disabled={!integrations?.links.public}
          onClick={() =>
            integrations?.links.public &&
            window.open(window.location.origin + integrations.links.public)
          }
          className="mt-2 w-full justify-between"
        >
          <span className="flex items-center gap-2">
            <LayoutPanelTop className="h-4 w-4" />
            {t('website:preview_controls.chat_page')}
          </span>
          <span className="flex items-center gap-1.5 text-foreground-subtle">
            {t('website:preview_link.open')}
            <ExternalLink className="h-4 w-4" />
          </span>
        </Button>
      </div>

      <div className="space-y-1.5" data-testid="chat-page-access-section">
        <Label data-testid="chat-page-access-title">{t('website:chat_page.title')}</Label>
        <p className="text-sm text-muted-foreground" data-testid="chat-page-access-description">
          {t('website:chat_page.description')}{' '}
          <Link
            to={`../${IndexPath.user.accountSettings}`}
            viewTransition
            className="text-foreground !underline !decoration-foreground underline-offset-2 hover:opacity-70"
            data-testid="invite-chat-users-link"
          >
            {t('website:chat_page.invite_link')}
          </Link>{' '}
          {t('website:chat_page.invite_suffix')}
        </p>
        <RadioGroupSegmented
          value={chatPageAccessData?.chat_page_access ?? 'public'}
          onValueChange={(value) => chatPageAccessMutation.mutate(value as ChatPageAccess)}
          data-testid="chat-page-access-select"
        >
          {(['public', 'restricted', 'disabled'] as const).map((value) => (
            <RadioGroupSegmentedItem key={value} value={value} id={`access-${value}`}>
              {t(`website:chat_page.access.${value}`)}
            </RadioGroupSegmentedItem>
          ))}
        </RadioGroupSegmented>
      </div>
    </div>
  );
};
